import { LoaderService } from "../../../shared/services/loader.service";
import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { debounceTime } from "rxjs/operator/debounceTime";
import { Subject, Subscription } from "rxjs/Rx";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { IMyDpOptions } from "mydatepicker";
import { IShift } from "./../../../helper/contracts";
import { Constants } from "./../../../helper/constants";
import { Shift } from "./../../../shared/services/shift/shift";
import { ShiftService } from "./../../../shared/services/shift/shift.service";
import { BackendService } from "./../../../shared/services/backend.service";
import { AuthService } from "./../../../shared/services/auth.service";

@Component({
  selector: "app-shifts",
  templateUrl: "./shifts.component.html",
  styleUrls: ["./shifts.component.css"]
})
export class ShiftsComponent implements OnInit, OnDestroy {
  currentDate = new Date();

  myDatePickerOptions: IMyDpOptions = {
    dateFormat: "yyyy-mm-dd",
    editableDateField: false,
    disableUntil: {
      year: this.currentDate.getFullYear(),
      month: this.currentDate.getMonth() + 1,
      day: this.currentDate.getDate() - 1
    }
  };

  communityId: string;
  routerSubscription;
  shifts: Array<IShift> = [];
  routeSubscription: Subscription = new Subscription();
  private _alert: Subject<string> = new Subject<string>();
  private alertSubscription: Subscription = new Subscription();
  modalReference: NgbModalRef;
  shift: IShift;
  confirmationTitle: string;
  confirmationMessage: string;
  actionType;
  successMessage: string;
  errorMessage: string;
  @Input("community") community;
  daysInWeek;
  shiftErrorMessage;
  query: string;
  operatorId: string;
  assignedShiftIds = [];

  constructor(
    private modalService: NgbModal,
    private loaderService: LoaderService,
    private shiftService: ShiftService,
    private authService: AuthService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.loaderService.startLoader();
    this.operatorId = BackendService.generateUserId(
      this.authService.getAuthState().email
    );
    this.daysInWeek = Object.keys(Constants.DAYS_OF_THE_WEEK);

    this.routeSubscription = this.route.params.subscribe(params => {
      this.communityId = params.communityId;
      this.shift = new Shift();
      this.shiftService
        .getShifts(this.communityId)
        .subscribe(shifts => (this.shifts = shifts));

      this.shiftService
        .getCommunityAssignedShiftIds(this.communityId)
        .subscribe(shiftIds => this.assignedShiftIds = shiftIds);

      this.loaderService.stopLoader();
    });
  }

  ngOnDestroy(): void {
    this.closeModal();
  }

  viewShift(modal, shift: IShift): void {
    this.shift = shift;
    this.openModal(modal, "md");
  }

  createOrEditShift(modal, shift: IShift): void {
    if (shift) {
      this.shift = shift;
      this.closeModal();
    } else {
      this.shift = new Shift();
      this.shift["isSpecial"] = false;
    }

    this.openModal(modal, "md");
  }

  async saveShift(): Promise<void> {
    this.loaderService.startLoader();

    if (this.isDuplicate() && !this.shift.id) {
      alert(
        `Opps, a shift with with name '${this.shift.name}' already exist, please change name and try again !`
      );
    } else {
      this.shift["isActive"] = true;
      if (this.shift.isSpecial) {
        this.shift["startDay"] = "none";
        this.shift["endDay"] = "none";
        this.shift["specialDay"] = this.shift["specialDay"].epoc * 1000;
      } else {
        this.shift["specialDay"] = 0;
        if (Constants.DAYS_OF_THE_WEEK[this.shift["startDay"]] > Constants.DAYS_OF_THE_WEEK[this.shift["endDay"]]) {

          this.showAlert(false, "Wrong configuration, start day cannot be greater than end day! Please configure from Sunday to Saturday.");
          this.closeModal();
          this.loaderService.stopLoader();

          return;
        }
      }

      if (this.shift.startTime > this.shift.endTime) {

        this.showAlert(false, "Wrong configuration, start time cannot be greater than end time!");
        this.closeModal();
        this.loaderService.stopLoader();

        return;
      }

      this.shift.operatorId = this.operatorId;
      this.shiftService.create(this.shift, this.communityId);
      if (this.shift.id) {
        const filteredShifts = this.shifts.filter(
          shift => shift.id !== this.shift.id
        );
        this.shifts = filteredShifts;
      }
      this.shifts.push(this.shift as IShift);
      const message = this.shift.id ? "Shift edited successfully" : "Shift created successfully";
      this.showAlert(true, message);
      this.closeModal();
    }

    this.loaderService.stopLoader();
  }

  isDuplicate(): boolean {
    const hasDuplicate = this.shifts.filter(shift =>
      shift.name.toLowerCase().trim() === this.shift.name.toLowerCase().trim()
    );

    return hasDuplicate.length ? true : false;
  }

  async deleteShift(): Promise<void> {
    const status = await this.shiftService.delete(this.communityId, this.shift.id);
    if (status) {
      this.showAlert(true, "Shift deleted successfully.");
    } else {
      this.showAlert(false, "Oops, unable to delete shift.");
    }
    this.closeModal();
  }

  async deactivateShift(): Promise<void> {
    const isAssigned = await this.shiftService.checkIsAssigned(
      this.shift,
      this.communityId
    );

    if (isAssigned) {
      this.closeModal();
      this.showAlert(
        false,
        "Opps, can't deactivate " + this.shift.name + ", it's been assigned already!"
      );
    } else {
      this.shift.isActive = false;
      this.shift.operatorId = this.operatorId;
      const status = await this.shiftService.update(
        this.shift,
        this.communityId
      );
      if (status) {
        this.shifts[this.shifts.indexOf(this.shift)].isActive = false;
        this.closeModal();
        this.showAlert(true, "Shift deactivated successfully.");
      } else {
        this.showAlert(
          false,
          "Opps, an error occured while updating shift status."
        );
      }
    }
  }

  async activateShift(): Promise<void> {
    this.shift.isActive = true;
    const status = await this.shiftService.update(this.shift, this.communityId);
    if (status) {
      this.shifts[this.shifts.indexOf(this.shift)].isActive = status;
      this.closeModal();
      this.showAlert(true, "Shift activated successfully.");
    } else {
      this.showAlert(
        false,
        "Opps, an error occured while updating shift status."
      );
    }
  }

  confirm(title, message, modal): void {
    this.confirmationTitle = title;
    this.confirmationMessage = message;
    this.closeModal();
    this.openModal(modal, "sm");
  }

  confirmAction(type, shift: any, modal): void {
    this.actionType = type;
    const message = `Are you sure you want to ${type} "${this.shift.name}"?`;
    this.confirm("Attention!", message, modal);
  }

  openModal(modal, modalSize): void {
    this.modalReference = this.modalService.open(modal, { size: modalSize });
  }

  closeModal(): void {
    this.shiftErrorMessage = null;
    if (this.modalReference) {
      this.modalReference.close();
    }
  }

  showAlert(msgType, msg): void {
    this.alertSubscription = this._alert.subscribe(message =>
      msgType ? (this.successMessage = message) : (this.errorMessage = message)
    );
    debounceTime
      .call(this._alert, 3000)
      .subscribe(() =>
        msgType
          ? (this.successMessage = undefined)
          : (this.errorMessage = undefined)
      );
    this._alert.next(msg);
    this.alertSubscription.unsubscribe();
  }
}



// WEBPACK FOOTER //
// ./src/app/community-management/community-resources/shifts/shifts.component.ts